declare const __CONFIGCAT_SDK_KEY__: string;

import * as configcat from "configcat-js";
import type { IConfigCatClient } from "configcat-js";
export { isMultiHopExecutorEnabled } from "../../shared/featureFlags/multiHopExecutor";
import {
  invalidateOfficeJsBypassFlag,
  isOfficeJsBypassed as readOfficeJsBypassState,
  markOfficeJsUnavailable as markSharedOfficeJsUnavailable,
  registerOfficeJsBypassFlagReader,
  resetOfficeJsBypassFlag,
  setOfficeJsBypassFlag,
} from "../../shared/office/officeJsBypass";
import {
  isBrandingFlagDiagEnabled,
  logBrandingFlagDiag,
  maskSdkKey,
} from "../../shared/featureFlags/brandingFlagDiag";
import {
  invalidateExcelClaudeOpusFlag,
  registerExcelClaudeOpusFlagReader,
  resetExcelClaudeOpusFlag,
  setExcelClaudeOpusFlag,
} from "../../shared/featureFlags/excelExecutorModel";
import {
  getActiveExcelBrandTemplateId,
  resetActiveExcelBrandTemplateId,
  setActiveExcelBrandTemplateId,
} from "../../shared/featureFlags/excelTemplateBrand";
import {
  DEFAULT_POWERPOINT_MODEL,
  POWERPOINT_MODEL_CONFIG_KEY,
  getPowerPointModel,
  resetPowerPointModel,
  setPowerPointModel,
} from "../../shared/remoteConfig/powerPointModel";
import { publishPluginModelPickerSnapshot } from "../../shared/featureFlags/pluginModelPicker";

const FLAG_BYPASS_OFFICE_JS = "bypassOfficeJsApi";
// Selects the model the Excel executor runs on: off serves GPT-6 Astra, on serves
// Claude Opus 5. Opus costs roughly 10x per query, so the SDK default stays
// `false` and every unresolved state reads as off. The resolved value is
// published to `shared/featureFlags/excelExecutorModel`, which Excel reads.
const FLAG_EXCEL_USE_CLAUDE_OPUS = "excelUseClaudeOpus";
// Fail-closed ConfigCat switch for PowerPoint ActiveGraphs. The add-in
// evaluates it with `isPowerPointActiveGraphsEnabled()`. Unresolved / no-SDK
// states stay off. The ConfigCat key is all-lowercase; a casing mismatch
// silently resolves to `false`.
const FLAG_PPTX_ACTIVE_GRAPHS = "pptxactivegraphs";
// Fail-closed ConfigCat switch for PowerPoint Preslink chart creation. Keep
// this separate from the existing Preslink refresh gate: creation has a
// different service dependency and routing contract.
const FLAG_PPTX_PRESLINK = "pptxpreslink";
const FLAG_PPTX_PRESLINK_SLIDE_LIBRARY = "pptxPreslinkSlideLibrary";
// Single ConfigCat text setting whose served value is a brand id (see
// `brandRegistry.ts`). Replaces the old one-boolean-flag-per-brand scheme
// (showPptxCitiBranding, showPptxCavendishBranding, ...): add a new client by
// appending a `BrandConfig` to the registry plus a new variation on this ONE
// flag, instead of creating a new ConfigCat flag every time.
const FLAG_PPTX_TEMPLATE_BRAND = "pptxTemplateBrand";
const DEFAULT_BRAND_TEMPLATE_ID = "";
const FLAG_EXCEL_TEMPLATE_BRAND = "excelTemplateBrand";
const DEFAULT_EXCEL_BRAND_TEMPLATE_ID = "";
// Internal-only model/reasoning controls shared by the PowerPoint and Excel
// task panes. Unresolved and missing-key states must never expose the picker.
const FLAG_PLUGIN_MODEL_PICKER = "pluginModelPicker";
const CONFIGCAT_POLL_INTERVAL_SECONDS = 120;
const UI_REFRESH_COOLDOWN_MS = 30_000;

export type FeatureFlagsInitStatus = "idle" | "initializing" | "ready" | "disabled";

interface FeatureFlagRequestContext {
  readonly client: IConfigCatClient;
  readonly generation: number;
  readonly accountScopeKey: string;
  readonly userEmail: string;
  readonly user: configcat.User | undefined;
}

let client: IConfigCatClient | null = null;
let currentUserEmail = "";
let currentAccountScopeKey = "";
let runtimeSdkKeyOverride = "";
let activeSdkKey = "";
let featureFlagsInitStatus: FeatureFlagsInitStatus = "idle";
let refreshAllPromise: Promise<void> | null = null;
let refreshAllGeneration: number | null = null;
let refreshAllIncludesRemoteFetch = false;
let featureFlagGeneration = 0;
let lastSuccessfulRemoteRefreshAt = 0;
let cachedBypassValue: boolean | null = null;
let cachedExcelClaudeOpusValue: boolean | null = null;
let cachedPptxActiveGraphsValue: boolean | null = null;
let cachedPptxPreslinkValue: boolean | null = null;
let cachedPptxPreslinkSlideLibraryValue: boolean | null = null;
let cachedPluginModelPickerValue: boolean | null = null;
let pluginModelPickerRefreshPending = true;
// Active brand-template id cache (null = not yet resolved). Mirrors the
// lazy-refresh contract of the other flags.
let cachedBrandTemplateId: string | null = null;
const loggedBrandingFlagWarnings = new Set<string>();

function setFeatureFlagsInitStatus(status: FeatureFlagsInitStatus): void {
  if (featureFlagsInitStatus === status) return;
  featureFlagsInitStatus = status;
  logBrandingFlagDiag("initStatusChanged", {
    status,
    userEmail: currentUserEmail || null,
  });
  notifyFeatureFlagListeners();
}

function logBrandingFlagSnapshot(reason: string): void {
  if (!isBrandingFlagDiagEnabled()) return;
  logBrandingFlagDiag("clientSnapshot", {
    reason,
    userEmail: currentUserEmail || null,
    initStatus: featureFlagsInitStatus,
    configCatClientActive: Boolean(client),
    bypassOfficeJsApi: cachedBypassValue,
    excelUseClaudeOpus: cachedExcelClaudeOpusValue,
    [FLAG_PPTX_ACTIVE_GRAPHS]: cachedPptxActiveGraphsValue,
    [FLAG_PPTX_PRESLINK]: cachedPptxPreslinkValue,
    [FLAG_PPTX_PRESLINK_SLIDE_LIBRARY]: cachedPptxPreslinkSlideLibraryValue,
    activeBrandTemplateId: cachedBrandTemplateId,
    activeExcelBrandTemplateId: getActiveExcelBrandTemplateId(),
    [POWERPOINT_MODEL_CONFIG_KEY]: getPowerPointModel(),
    [FLAG_PLUGIN_MODEL_PICKER]: cachedPluginModelPickerValue,
  });
}

function clearBrandTemplateCache(): void {
  cachedBrandTemplateId = null;
}
clearBrandTemplateCache();

function resetAllFlagCaches(): void {
  cachedBypassValue = null;
  resetOfficeJsBypassFlag();
  // Init and logout both drop the Excel executor back to GPT-6 Astra rather than
  // carrying the previous session's Opus entitlement into the next one.
  cachedExcelClaudeOpusValue = null;
  resetExcelClaudeOpusFlag();
  cachedPptxActiveGraphsValue = null;
  cachedPptxPreslinkValue = null;
  cachedPptxPreslinkSlideLibraryValue = null;
  clearBrandTemplateCache();
  resetActiveExcelBrandTemplateId();
  resetPowerPointModel();
  pluginModelPickerRefreshPending = true;
  cachedPluginModelPickerValue = null;
  publishPluginModelPickerState();
}

function invalidateBypassFlagCache(): void {
  cachedBypassValue = null;
  invalidateOfficeJsBypassFlag();
}

function invalidateExcelClaudeOpusFlagCache(): void {
  cachedExcelClaudeOpusValue = null;
  invalidateExcelClaudeOpusFlag();
}

function invalidatePluginModelPickerFlagCache(): void {
  pluginModelPickerRefreshPending = true;
  cachedPluginModelPickerValue = null;
  publishPluginModelPickerState();
}

function invalidateActiveGraphFlagCache(): void {
  cachedPptxActiveGraphsValue = null;
}

function invalidatePreslinkSlideLibraryFlagCache(): void {
  cachedPptxPreslinkSlideLibraryValue = null;
}

function publishPluginModelPickerState(): void {
  publishPluginModelPickerSnapshot({
    ready:
      Boolean(client) &&
      featureFlagsInitStatus === "ready" &&
      !pluginModelPickerRefreshPending &&
      cachedPluginModelPickerValue !== null,
    enabled: cachedPluginModelPickerValue === true,
    accountScopeKey: currentAccountScopeKey,
    contextGeneration: featureFlagGeneration,
  });
}

registerOfficeJsBypassFlagReader(readFeatureFlagOfficeJsBypass);
registerExcelClaudeOpusFlagReader(readFeatureFlagExcelClaudeOpus);

type FeatureFlagListener = () => void;
const listeners = new Set<FeatureFlagListener>();

function notifyFeatureFlagListeners(): void {
  publishPluginModelPickerState();
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn("[FeatureFlags] Listener threw", err);
    }
  });
}

/**
 * Subscribe to feature-flag cache changes. Listeners fire when the
 * ConfigCat client reports a config change, when a lazy refresh
 * resolves to a different value, or when the client is disposed
 * (e.g. logout).
 *
 * Used by the `useActiveBrand` hook (via `useSyncExternalStore`)
 * so React components re-render when remote flags flip mid-session.
 */
export function subscribeFeatureFlags(listener: FeatureFlagListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getFeatureFlagsInitStatus(): FeatureFlagsInitStatus {
  return featureFlagsInitStatus;
}

export function areBrandingFeatureFlagsReady(): boolean {
  return featureFlagsInitStatus === "ready";
}

export async function refreshFeatureFlagsForUi(reason: string = "uiRefresh"): Promise<void> {
  if (!client || !currentUserEmail || featureFlagsInitStatus === "disabled") {
    logBrandingFlagDiag("uiRefreshSkipped", {
      reason,
      userEmail: currentUserEmail || null,
      initStatus: featureFlagsInitStatus,
      clientActive: Boolean(client),
    });
    return;
  }

  if (refreshAllPromise && refreshAllIncludesRemoteFetch) {
    logBrandingFlagDiag("uiRefreshJoined", {
      reason,
      userEmail: currentUserEmail,
    });
    return refreshAllPromise;
  }

  const now = Date.now();
  if (
    lastSuccessfulRemoteRefreshAt &&
    now - lastSuccessfulRemoteRefreshAt < UI_REFRESH_COOLDOWN_MS
  ) {
    logBrandingFlagDiag("uiRefreshThrottled", {
      reason,
      userEmail: currentUserEmail,
      msSinceLastRefresh: now - lastSuccessfulRemoteRefreshAt,
      cooldownMs: UI_REFRESH_COOLDOWN_MS,
    });
    return;
  }

  await refreshAllFeatureFlags(reason, featureFlagGeneration, { forceRemote: true });
}

function getConfigCatSdkKey(): string {
  // Prefer the rspack-injected build-time key (per-env GitHub secret at CI).
  // Fall back to the session key when the build key is absent (e.g. local without
  // CONFIGCAT_SDK_KEY, or older bundles).
  const buildKey = typeof __CONFIGCAT_SDK_KEY__ !== "undefined" ? __CONFIGCAT_SDK_KEY__ : "";
  return buildKey || runtimeSdkKeyOverride;
}

function createConfigCatClient(
  sdkKey: string,
  generation: number,
  accountScopeKey: string
): IConfigCatClient {
  let createdClient: IConfigCatClient | null = null;
  createdClient = configcat.getClient(sdkKey, configcat.PollingMode.AutoPoll, {
    pollIntervalSeconds: CONFIGCAT_POLL_INTERVAL_SECONDS,
    logger: configcat.createConsoleLogger(configcat.LogLevel.Warn),
    setupHooks: (hooks) => {
      hooks.on("configChanged", () => {
        if (!createdClient) return;
        handleConfigChanged(createdClient, generation, accountScopeKey);
      });
    },
  });
  return createdClient;
}

function handleConfigChanged(
  requestClient: IConfigCatClient,
  requestGeneration: number,
  requestAccountScopeKey: string
): void {
  if (
    !isFeatureFlagContextCurrent(
      requestClient,
      requestGeneration,
      requestAccountScopeKey
    )
  ) {
    return;
  }

  const wasReady = featureFlagsInitStatus === "ready";
  invalidateBypassFlagCache();
  invalidateExcelClaudeOpusFlagCache();
  invalidatePreslinkSlideLibraryFlagCache();
  // This internal-only control is deliberately stricter than ordinary UI
  // flags: any config change closes it until the current account re-resolves
  // pluginModelPicker with the SDK's false fallback.
  invalidatePluginModelPickerFlagCache();
  if (featureFlagsInitStatus !== "ready") {
    invalidateActiveGraphFlagCache();
    invalidatePreslinkSlideLibraryFlagCache();
    clearBrandTemplateCache();
    setFeatureFlagsInitStatus("initializing");
  }
  if (wasReady) notifyFeatureFlagListeners();
  console.log("[FeatureFlags] Config changed — cache invalidated");
  logBrandingFlagDiag("configChanged", { userEmail: currentUserEmail });
  refreshAllFeatureFlags("configChanged", requestGeneration).catch((error) => {
    if (
      !isFeatureFlagContextCurrent(
        requestClient,
        requestGeneration,
        requestAccountScopeKey
      )
    ) {
      return;
    }
    logBrandingFlagDiag("configChangedRefreshFailed", {
      userEmail: currentUserEmail,
      error: error instanceof Error ? error.message : String(error),
    });
  });
}

async function forceRefreshRemoteConfig(
  reason: string,
  request: FeatureFlagRequestContext
): Promise<boolean> {
  if (!isFeatureFlagRequestCurrent(request)) return false;

  logBrandingFlagDiag("remoteRefreshStart", {
    reason,
    userEmail: request.userEmail || null,
  });

  let result: Awaited<ReturnType<IConfigCatClient["forceRefreshAsync"]>>;
  try {
    result = await request.client.forceRefreshAsync();
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) return false;
    throw error;
  }

  if (!isFeatureFlagRequestCurrent(request)) return false;

  if (!result.isSuccess) {
    const errorMessage = result.errorMessage || "ConfigCat force refresh failed";
    logBrandingFlagDiag("remoteRefreshFailed", {
      reason,
      userEmail: request.userEmail || null,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  logBrandingFlagDiag("remoteRefreshSucceeded", {
    reason,
    userEmail: request.userEmail || null,
  });
  return true;
}

/**
 * Call once at startup when the Office JS runtime cannot be loaded at all.
 * After this, `isOfficeJsBypassed()` returns `true` unconditionally.
 */
export function markOfficeJsUnavailable(): void {
  markSharedOfficeJsUnavailable();
  console.log("[FeatureFlags] Office JS marked as unavailable — all Office calls will be NOPs");
}

export async function initFeatureFlags(
  userEmail: string,
  options?: {
    sdkKey?: string;
    accountScopeKey?: string | null;
    companyId?: string | null;
  }
): Promise<void> {
  if (options?.sdkKey?.trim()) {
    runtimeSdkKeyOverride = options.sdkKey.trim();
  }

  const sdkKey = getConfigCatSdkKey();
  const sdkKeyChanged = client !== null && activeSdkKey !== sdkKey;
  const companyId = options?.companyId?.trim() || "";
  const accountScopeKey =
    options?.accountScopeKey?.trim() ||
    JSON.stringify([userEmail.trim(), companyId || null]);

  if (
    currentUserEmail === userEmail &&
    currentAccountScopeKey === accountScopeKey &&
    client &&
    !sdkKeyChanged &&
    (featureFlagsInitStatus === "ready" ||
      (featureFlagsInitStatus === "initializing" && refreshAllPromise))
  ) {
    logBrandingFlagDiag("initSkipped", {
      userEmail,
      companyId: companyId || null,
      reason: "alreadyInitialized",
    });
    return;
  }

  logBrandingFlagDiag("initStart", {
    userEmail,
    companyId: companyId || null,
    sdkKey: maskSdkKey(sdkKey),
    sdkKeySource:
      typeof __CONFIGCAT_SDK_KEY__ !== "undefined" && __CONFIGCAT_SDK_KEY__
        ? "buildTime"
        : runtimeSdkKeyOverride
          ? "session"
          : "none",
    brandFlagKey: FLAG_PPTX_TEMPLATE_BRAND,
  });

  currentUserEmail = userEmail;
  currentAccountScopeKey = accountScopeKey;
  featureFlagGeneration += 1;
  const initGeneration = featureFlagGeneration;
  refreshAllPromise = null;
  refreshAllGeneration = null;
  refreshAllIncludesRemoteFetch = false;
  lastSuccessfulRemoteRefreshAt = 0;
  setFeatureFlagsInitStatus("initializing");
  resetAllFlagCaches();

  if (!sdkKey) {
    console.warn("[FeatureFlags] No ConfigCat SDK key configured — flags disabled");
    activeSdkKey = "";
    setFeatureFlagsInitStatus("disabled");
    logBrandingFlagDiag("initAborted", {
      userEmail,
      reason: "missingSdkKey",
      hint: "Set CONFIGCAT_SDK_KEY in .env and restart the dev server, or ensure /api/auth/session returns features.configcatSdkKey.",
    });
    return;
  }

  if (client) {
    client.dispose();
    client = null;
  }

  client = createConfigCatClient(sdkKey, initGeneration, accountScopeKey);
  activeSdkKey = sdkKey;

  logBrandingFlagDiag("clientCreated", {
    userEmail,
    pollIntervalSeconds: CONFIGCAT_POLL_INTERVAL_SECONDS,
  });

  try {
    await refreshAllFeatureFlags("initComplete", initGeneration, { forceRemote: true });
  } catch (error) {
    logBrandingFlagDiag("initFetchFailed", {
      userEmail,
      error: error instanceof Error ? error.message : String(error),
    });
    try {
      await new Promise((r) => setTimeout(r, 1000));
      await refreshAllFeatureFlags("initCompleteAfterRetry", initGeneration, { forceRemote: true });
    } catch (retryError) {
      console.warn(
        "[FeatureFlags] Initial remote flag refresh failed after retry — branding UI remains hidden until auto-poll succeeds"
      );
      logBrandingFlagDiag("initFetchFailedAfterRetry", {
        userEmail,
        error: retryError instanceof Error ? retryError.message : String(retryError),
      });
    }
  }
}

async function refreshAllFeatureFlags(
  reason: string,
  generation: number,
  options: { forceRemote?: boolean } = {}
): Promise<void> {
  const forceRemote = options.forceRemote === true;
  const request = captureFeatureFlagRequest();
  if (!request || request.generation !== generation) {
    logBrandingFlagDiag("refreshSkipped", {
      reason,
      generation,
      currentGeneration: featureFlagGeneration,
      clientActive: Boolean(client),
    });
    return;
  }

  if (refreshAllPromise && refreshAllGeneration === generation) {
    if (!forceRemote || refreshAllIncludesRemoteFetch) {
      return refreshAllPromise;
    }

    await refreshAllPromise;
    if (!isFeatureFlagRequestCurrent(request)) {
      logBrandingFlagDiag("refreshSkippedAfterJoinedRefresh", {
        reason,
        generation,
        currentGeneration: featureFlagGeneration,
        clientActive: Boolean(client),
      });
      return;
    }
  }

  const refreshPromise = (async () => {
    if (forceRemote) {
      const refreshed = await forceRefreshRemoteConfig(reason, request);
      if (!refreshed) return;
    }

    if (!isFeatureFlagRequestCurrent(request)) return;

    await Promise.all([
      refreshBypassFlag(request),
      refreshBrandTemplateId(request),
      refreshExcelBrandTemplateId(request),
      refreshPowerPointModelConfig(request),
      refreshPluginModelPickerFlag(request),
      refreshExcelClaudeOpusFlag(request),
      refreshActiveGraphFlags(request),
      refreshPreslinkChartFlag(request),
      refreshPreslinkSlideLibraryFlag(request),
    ]);
    if (!isFeatureFlagRequestCurrent(request)) {
      logBrandingFlagDiag("refreshIgnored", {
        reason,
        generation,
        currentGeneration: featureFlagGeneration,
        clientActive: Boolean(client),
      });
      return;
    }
    pluginModelPickerRefreshPending = false;
    publishPluginModelPickerState();
    if (forceRemote) {
      lastSuccessfulRemoteRefreshAt = Date.now();
    }
    setFeatureFlagsInitStatus("ready");
    logBrandingFlagSnapshot(reason);
  })();
  refreshAllPromise = refreshPromise;
  refreshAllGeneration = generation;
  refreshAllIncludesRemoteFetch = forceRemote;

  try {
    await refreshPromise;
  } finally {
    if (refreshAllPromise === refreshPromise) {
      refreshAllPromise = null;
      refreshAllGeneration = null;
      refreshAllIncludesRemoteFetch = false;
    }
  }
}

export function disposeFeatureFlags(): void {
  if (client) {
    client.dispose();
    client = null;
  }
  resetAllFlagCaches();
  loggedBrandingFlagWarnings.clear();
  activeSdkKey = "";
  refreshAllPromise = null;
  refreshAllGeneration = null;
  refreshAllIncludesRemoteFetch = false;
  lastSuccessfulRemoteRefreshAt = 0;
  featureFlagGeneration += 1;
  logBrandingFlagDiag("disposed", { userEmail: currentUserEmail || null });
  currentUserEmail = "";
  currentAccountScopeKey = "";
  runtimeSdkKeyOverride = "";
  featureFlagsInitStatus = "idle";
  notifyFeatureFlagListeners();
}

function buildUser(): configcat.User | undefined {
  if (!currentUserEmail) return undefined;
  return new configcat.User(currentUserEmail, currentUserEmail);
}

function captureFeatureFlagRequest(): FeatureFlagRequestContext | null {
  const requestClient = client;
  if (!requestClient) return null;

  return {
    client: requestClient,
    generation: featureFlagGeneration,
    accountScopeKey: currentAccountScopeKey,
    userEmail: currentUserEmail,
    user: buildUser(),
  };
}

function isFeatureFlagContextCurrent(
  requestClient: IConfigCatClient,
  requestGeneration: number,
  requestAccountScopeKey: string
): boolean {
  return (
    requestClient === client &&
    requestGeneration === featureFlagGeneration &&
    requestAccountScopeKey === currentAccountScopeKey
  );
}

function isFeatureFlagRequestCurrent(request: FeatureFlagRequestContext): boolean {
  return isFeatureFlagContextCurrent(
    request.client,
    request.generation,
    request.accountScopeKey
  );
}

async function readConfigCatValue<T extends boolean | string>(
  request: FeatureFlagRequestContext,
  flagKey: string,
  defaultValue: T
): Promise<T | null> {
  if (!isFeatureFlagRequestCurrent(request)) return null;

  try {
    const value = (await request.client.getValueAsync(
      flagKey,
      defaultValue,
      request.user
    )) as T;
    return isFeatureFlagRequestCurrent(request) ? value : null;
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) return null;
    throw error;
  }
}

async function refreshBypassFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedBypassValue = false;
    setOfficeJsBypassFlag(false);
    return false;
  }

  const value = await readConfigCatValue(request, FLAG_BYPASS_OFFICE_JS, false);
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedBypassValue !== value;
  cachedBypassValue = value;
  setOfficeJsBypassFlag(value);
  console.log(`[FeatureFlags] ${FLAG_BYPASS_OFFICE_JS} = ${value} (user: ${currentUserEmail})`);
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshExcelClaudeOpusFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedExcelClaudeOpusValue = false;
    setExcelClaudeOpusFlag(false);
    return false;
  }

  const value = await readConfigCatValue(request, FLAG_EXCEL_USE_CLAUDE_OPUS, false);
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedExcelClaudeOpusValue !== value;
  cachedExcelClaudeOpusValue = value;
  setExcelClaudeOpusFlag(value);
  console.log(
    `[FeatureFlags] ${FLAG_EXCEL_USE_CLAUDE_OPUS} = ${value} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshActiveGraphFlags(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedPptxActiveGraphsValue = false;
    return false;
  }
  const enabled = await readConfigCatValue(request, FLAG_PPTX_ACTIVE_GRAPHS, false);
  if (enabled === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedPptxActiveGraphsValue !== enabled;
  cachedPptxActiveGraphsValue = enabled;
  console.log(
    `[FeatureFlags] ${FLAG_PPTX_ACTIVE_GRAPHS} = ${enabled} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return enabled;
}

async function refreshPreslinkChartFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedPptxPreslinkValue = false;
    return false;
  }
  const enabled = await readConfigCatValue(request, FLAG_PPTX_PRESLINK, false);
  if (enabled === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedPptxPreslinkValue !== enabled;
  cachedPptxPreslinkValue = enabled;
  console.log(
    `[FeatureFlags] ${FLAG_PPTX_PRESLINK} = ${enabled} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return enabled;
}

async function refreshPreslinkSlideLibraryFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedPptxPreslinkSlideLibraryValue = false;
    return false;
  }
  const enabled = await readConfigCatValue(request, FLAG_PPTX_PRESLINK_SLIDE_LIBRARY, false);
  if (enabled === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedPptxPreslinkSlideLibraryValue !== enabled;
  cachedPptxPreslinkSlideLibraryValue = enabled;
  console.log(
    `[FeatureFlags] ${FLAG_PPTX_PRESLINK_SLIDE_LIBRARY} = ${enabled} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return enabled;
}

async function refreshBrandTemplateId(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<string> {
  if (!request) {
    cachedBrandTemplateId = DEFAULT_BRAND_TEMPLATE_ID;
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const value = await readConfigCatValue(
    request,
    FLAG_PPTX_TEMPLATE_BRAND,
    DEFAULT_BRAND_TEMPLATE_ID
  );
  if (value === null || !isFeatureFlagRequestCurrent(request)) {
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const changed = cachedBrandTemplateId !== value;
  cachedBrandTemplateId = value;
  console.log(`[FeatureFlags] ${FLAG_PPTX_TEMPLATE_BRAND} = ${value} (user: ${currentUserEmail})`);
  logBrandingFlagDiag("brandingFlagRefreshed", {
    flagKey: FLAG_PPTX_TEMPLATE_BRAND,
    brandId: value || null,
    value,
    userEmail: currentUserEmail,
    changed,
  });
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshExcelBrandTemplateId(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<string> {
  if (!request) {
    return getActiveExcelBrandTemplateId();
  }

  try {
    const value = await readConfigCatValue(
      request,
      FLAG_EXCEL_TEMPLATE_BRAND,
      DEFAULT_EXCEL_BRAND_TEMPLATE_ID
    );

    if (value === null || !isFeatureFlagRequestCurrent(request)) {
      return getActiveExcelBrandTemplateId();
    }

    setActiveExcelBrandTemplateId(value);
    const activeValue = getActiveExcelBrandTemplateId();
    console.log(
      `[FeatureFlags] ${FLAG_EXCEL_TEMPLATE_BRAND} = ${activeValue} (user: ${currentUserEmail})`
    );
    return activeValue;
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) {
      return getActiveExcelBrandTemplateId();
    }

    const activeValue = getActiveExcelBrandTemplateId();
    console.warn(
      `[FeatureFlags] Failed to refresh ${FLAG_EXCEL_TEMPLATE_BRAND}; keeping ${activeValue || "(empty)"}`,
      error
    );
    logBrandingFlagDiag("excelBrandingFlagRefreshFailed", {
      flagKey: FLAG_EXCEL_TEMPLATE_BRAND,
      brandId: activeValue || null,
      userEmail: currentUserEmail,
      error: error instanceof Error ? error.message : String(error),
    });
    return activeValue;
  }
}

async function refreshPowerPointModelConfig(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<string> {
  if (!request) {
    resetPowerPointModel();
    return getPowerPointModel();
  }

  const value = await readConfigCatValue(
    request,
    POWERPOINT_MODEL_CONFIG_KEY,
    DEFAULT_POWERPOINT_MODEL
  );
  if (value === null || !isFeatureFlagRequestCurrent(request)) {
    return getPowerPointModel();
  }

  setPowerPointModel(value);
  const activeModel = getPowerPointModel();
  console.log(
    `[RemoteConfig] ${POWERPOINT_MODEL_CONFIG_KEY} = ${activeModel} (user: ${currentUserEmail})`
  );
  return activeModel;
}

async function refreshPluginModelPickerFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedPluginModelPickerValue = false;
    publishPluginModelPickerState();
    return false;
  }

  const value = await readConfigCatValue(request, FLAG_PLUGIN_MODEL_PICKER, false);
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedPluginModelPickerValue !== value;
  cachedPluginModelPickerValue = value;
  console.log(
    `[FeatureFlags] ${FLAG_PLUGIN_MODEL_PICKER} = ${value} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return value;
}

/**
 * Synchronous check for whether Office JS calls should be bypassed.
 * Returns `true` when:
 *   1. Office JS runtime is not loaded at all, OR
 *   2. The ConfigCat `bypassOfficeJsApi` flag is enabled for the current user.
 * Safe to call on every Office JS entry point — no await required.
 */
export function isOfficeJsBypassed(): boolean {
  return readOfficeJsBypassState();
}

/**
 * Synchronous add-in gate for PowerPoint ActiveGraphs. Unresolved, disabled,
 * and no-SDK states fail closed.
 */
export function isPowerPointActiveGraphsEnabled(): boolean {
  if (!client || featureFlagsInitStatus !== "ready") return false;
  return cachedPptxActiveGraphsValue === true;
}

/** Synchronous, fail-closed rollout gate for PowerPoint Preslink creation. */
export function isPowerPointPreslinkCreationEnabled(): boolean {
  if (!client || featureFlagsInitStatus !== "ready") return false;
  return cachedPptxPreslinkValue === true;
}

/** Synchronous, fail-closed rollout gate for the PresLink Slide Library preview. */
export function isPowerPointPreslinkSlideLibraryEnabled(): boolean {
  if (!client || featureFlagsInitStatus !== "ready") return false;
  return cachedPptxPreslinkSlideLibraryValue === true;
}

export interface PowerPointChartRoutingGateSnapshot {
  readonly activeGraphEnabled: boolean;
  readonly preslinkEnabled: boolean;
}

/**
 * Atomically captures both chart-routing flags for one submission so a remote
 * refresh cannot change the default chart route midway through a turn.
 */
export function snapshotPowerPointChartRoutingGate(): PowerPointChartRoutingGateSnapshot {
  return Object.freeze({
    activeGraphEnabled: isPowerPointActiveGraphsEnabled(),
    preslinkEnabled: isPowerPointPreslinkCreationEnabled(),
  });
}

export interface PowerPointActiveGraphGateSnapshot {
  readonly enabled: boolean;
  readonly contextGeneration: number;
}

export function snapshotPowerPointActiveGraphGate(): PowerPointActiveGraphGateSnapshot {
  return Object.freeze({
    enabled: isPowerPointActiveGraphsEnabled(),
    contextGeneration: featureFlagGeneration,
  });
}

export function isPowerPointActiveGraphGateSnapshotCurrent(
  snapshot: PowerPointActiveGraphGateSnapshot
): boolean {
  return snapshot.contextGeneration === featureFlagGeneration;
}

function readFeatureFlagOfficeJsBypass(): boolean {
  if (!client) return false;

  if (cachedBypassValue !== null) {
    return cachedBypassValue;
  }

  if (refreshAllPromise) {
    return false;
  }

  refreshBypassFlag().catch(() => {});
  return false;
}

function readFeatureFlagExcelClaudeOpus(): boolean {
  if (!client) return false;

  if (cachedExcelClaudeOpusValue !== null) {
    return cachedExcelClaudeOpusValue;
  }

  if (refreshAllPromise) {
    return false;
  }

  refreshExcelClaudeOpusFlag().catch(() => {});
  return false;
}

/**
 * Synchronous read of the active brand-template id for the current user, as
 * served by the single `pptxTemplateBrand` ConfigCat text setting. Returns
 * `""` (no brand) until ConfigCat resolves the flag for the current user.
 *
 * Safe to call from a render path — pair with `subscribeFeatureFlags`
 * (e.g. via the `useActiveBrand` hook) so components re-render when the flag
 * flips mid-session.
 */
export function getActiveBrandTemplateId(): string {
  const flagKey = FLAG_PPTX_TEMPLATE_BRAND;
  if (!client) {
    const warningKey =
      featureFlagsInitStatus === "disabled"
        ? "disabled"
        : featureFlagsInitStatus === "idle"
          ? "notStarted"
          : featureFlagsInitStatus === "initializing"
            ? "initializing"
            : "noClient";
    if (!loggedBrandingFlagWarnings.has(warningKey)) {
      loggedBrandingFlagWarnings.add(warningKey);
      logBrandingFlagDiag("brandingFlagCheck", {
        flagKey,
        result: DEFAULT_BRAND_TEMPLATE_ID,
        reason:
          featureFlagsInitStatus === "disabled"
            ? "configCatClientDisabled"
            : featureFlagsInitStatus === "idle"
              ? "initNotStartedYet"
              : featureFlagsInitStatus === "initializing"
                ? "initInProgress"
                : "configCatClientNotInitialized",
        initStatus: featureFlagsInitStatus,
        hint:
          featureFlagsInitStatus === "disabled"
            ? "initAborted/missingSdkKey — check initStart log for sdkKeySource."
            : featureFlagsInitStatus === "idle"
              ? "UI rendered before initFeatureFlags() useEffect; should resolve after login init."
              : featureFlagsInitStatus === "initializing"
                ? "ConfigCat initialization is still resolving; branding UI remains hidden until ready."
                : "initFeatureFlags() ran but client is missing; check initAborted logs.",
      });
    }
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  if (cachedBrandTemplateId !== null) {
    return cachedBrandTemplateId;
  }

  if (refreshAllPromise) {
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const pendingKey = `pending:${flagKey}`;
  if (!loggedBrandingFlagWarnings.has(pendingKey)) {
    loggedBrandingFlagWarnings.add(pendingKey);
    logBrandingFlagDiag("brandingFlagCheck", {
      flagKey,
      result: DEFAULT_BRAND_TEMPLATE_ID,
      reason: "cachePending",
      hint: "Flag not resolved yet; awaiting refreshBrandTemplateId().",
    });
  }

  refreshBrandTemplateId().catch(() => {});
  return DEFAULT_BRAND_TEMPLATE_ID;
}
